import React, { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { func, bool } from 'prop-types'
import Button from '@ingka/button'
import SSRIcon from '@ingka/ssr-icon'
import shoppingBagAdd from '@ingka/ssr-icon/paths/shopping-bag-add'

import { productDataType } from 'models/propTypes'
import * as RenderItemTypes from 'constants/renderItemTypes'
import RelatedProductsContainer from 'containers/designSidepanel/RelatedProductsContainer'
import Icon from 'components/uiElements/Icon'
import BackNavigationHeader from 'components/designSidepanel/BackNavigationHeader'
import Tooltip from 'components/Tooltip'
import SugarcubeContext from 'apps/SugarcubeContext'
import OptionSelectorViewContainer from 'containers/designSidepanel/OptionSelectorViewContainer'
import FavoriteButtonContainer from 'containers/uiElements/FavoriteButtonContainer'
import { TRACK_LOCATION_SIDEPANEL } from 'models/tracking/constants'
import AdminProductDetailsContainer from 'containers/admin/AdminProductDetailsContainer'
import ProductSummary from 'components/designSidepanel/ProductSummary'
import ImagePicker from './ImagePicker'

require('./ItemDetailsView.scss')

const ItemDetailsView = ({
    productData,
    deleteItem,
    addToBag,
    trackShowDetails,
    swapItem,
    disableSwapCta,
    onBackClick,
    isInSwap,
}) => {
    const { t } = useTranslation()

    const { name, thumbUrl, imageUrls, localItemNumber } = productData
    const { isMagicWindow, isMobile, isKiosk } = useContext(SugarcubeContext)
    const navigationText = isInSwap
        ? `${t('design.swap list')} > ${name.toUpperCase()}`
        : t('design.products in this room')
    const showAddToBagPrimary = addToBag && !isInSwap && !isKiosk

    const ctaContainer = (
        <div className="ctaContainer">
            {isInSwap && (
                <>
                    <Button
                        type="primary"
                        className="swapCta"
                        onClick={swapItem}
                        disabled={disableSwapCta}
                        data-testid="item-details-swap-btn"
                    >
                        {t('design.swap')}
                    </Button>

                    {!isKiosk && (
                        <Button
                            className="cartCta"
                            type="secondary"
                            onClick={addToBag}
                            data-testid="item-details-add-to-bag-btn"
                            iconOnly
                        >
                            <SSRIcon paths={shoppingBagAdd} />
                        </Button>
                    )}
                </>
            )}
            {showAddToBagPrimary && (
                <button className="primaryCta" onClick={addToBag} data-testid="item-details-add-to-bag-btn">
                    <span>{t('design.add to bag')}</span>
                </button>
            )}
            <div className="secondaryCTAs">
                <FavoriteButtonContainer localItemNumber={localItemNumber} location={TRACK_LOCATION_SIDEPANEL} />
                {deleteItem && (
                    <>
                        <Tooltip id="myItemsDetailsViewDeleteBtn" />
                        <button
                            className="trash"
                            onClick={deleteItem}
                            data-tip={t('tooltip.remove this product')}
                            data-for="myItemsDetailsViewDeleteBtn"
                        >
                            <Icon code="trashCan" />
                        </button>
                    </>
                )}
            </div>
        </div>
    )

    return (
        <div className="ItemDetailsView">
            <BackNavigationHeader onClick={onBackClick} navigationText={navigationText} />
            <div className="content">
                <div className="itemSummary">
                    <div className="thumbContainer">
                        <ImagePicker images={imageUrls} thumbUrl={thumbUrl} name={name} />
                    </div>
                    <div>
                        <ProductSummary productData={productData} trackShowDetails={trackShowDetails} />
                        <AdminProductDetailsContainer productData={productData} />
                        {isMobile && ctaContainer}
                    </div>
                </div>
                {!isMobile && ctaContainer}
                {!isInSwap && productData.renderType === RenderItemTypes.ART_ITEM && (
                    <div className="wallDecorOptions">
                        <OptionSelectorViewContainer />
                    </div>
                )}
                {!isMagicWindow && <RelatedProductsContainer />}
            </div>
        </div>
    )
}

ItemDetailsView.defaultProps = {
    addToBag: null,
    deleteItem: null,
    swapItem: null,
    disableSwapCta: false,
    isInSwap: false,
}

ItemDetailsView.propTypes = {
    productData: productDataType.isRequired,
    deleteItem: func,
    addToBag: func,
    trackShowDetails: func.isRequired,
    isInSwap: bool,
    swapItem: func,
    disableSwapCta: bool,
    onBackClick: func.isRequired,
}

export default ItemDetailsView
